import type { TFunction } from 'i18next'
import type { BoardWrite } from '@ludus/core'
import { refusedWriteMessage } from '@/shared/hooks/usePipelineAdapter.ts'

/**
 * A typed API error's `kind` → the sentence a form shows. Callers pass the i18n
 * prefix of their own `error*` keys: *what* failed stays the form's own words,
 * while a new error kind is handled here instead of in every dialog that ever
 * submits. A form with nothing specific to say about a 404 falls through to its
 * generic line rather than carrying a key it never shows.
 *
 * The parameter is `{ kind: string }` rather than one error class: every
 * `lib/api/*` error carries a `kind`, the three this reads mean the same thing
 * in all of them, and the kinds that do not appear here already fall to the
 * generic line. Naming one class would make the second one need a copy.
 */
export function apiErrorMessage(
  err: { kind: string; write?: BoardWrite | null; source?: string | null },
  t: TFunction,
  keyPrefix: string,
): string {
  const refused = refusedWriteMessage(t, err)
  if (refused !== null) return refused
  const generic = t(`${keyPrefix}.errorGeneric`, {
    defaultValue: 'Something went wrong. Please try again.',
  })
  switch (err.kind) {
    case 'unauthorized':
      return t(`${keyPrefix}.errorUnauthorized`, {
        defaultValue: 'Session expired — refresh the page and try again.',
      })
    case 'not_found':
      return t(`${keyPrefix}.errorNotFound`, { defaultValue: generic })
    case 'network':
      return t(`${keyPrefix}.errorNetwork`, {
        defaultValue: 'Network error — check your connection and retry.',
      })
    default:
      return generic
  }
}
